import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { FileWarning, Loader2, UploadCloud } from "lucide-react";

export const ReuploadPages = ({ jobId, pending, onStarted }) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (!pending?.length) return null;

  const submit = async () => {
    if (!files.length) return toast.error("Choose the page file(s) to re-upload");
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (password.trim()) fd.append("password", password.trim());
    try {
      await api.post(`/analysis/${jobId}/reupload-pages`, fd);
      toast.success("Re-processing the failed pages — no credits charged");
      setFiles([]);
      onStarted?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-warn-border bg-warn p-4 text-sm text-warn-foreground" data-testid="reupload-pages-panel">
      <div className="flex items-start gap-3">
        <FileWarning className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Some pages could not be read</p>
          <ul className="mt-1 space-y-0.5 text-xs" data-testid="reupload-pending-list">
            {pending.map((p) => (
              <li key={p.filename} className="truncate">
                <span className="font-medium">{p.filename}</span> — page{p.pages.length > 1 ? "s" : ""} {p.pages.join(", ")}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs">
            The other pages were processed. Export or print just the pages above (PDF or photos) and re-upload them
            here — they will be merged into this analysis at no extra credit.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input ref={inputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                   onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="reupload-file-input" />
            <Button type="button" variant="outline" size="sm" className="rounded-full bg-white"
                    onClick={() => inputRef.current?.click()} data-testid="reupload-choose-btn">
              <UploadCloud className="mr-2 h-4 w-4" /> {files.length ? `${files.length} file(s) selected` : "Choose page file(s)"}
            </Button>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off"
                   placeholder="PDF password (optional)" className="h-8 w-48 bg-white text-xs" data-testid="reupload-password-input" />
            <Button type="button" size="sm" className="rounded-full" onClick={submit} disabled={busy || !files.length}
                    data-testid="reupload-submit-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Re-process pages
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
